import React, { useCallback, useMemo, useEffect } from 'react'
import { useLocation, useQueryParams } from '@fs/zion-router'
import PropTypes from 'prop-types'
import { Paragraph, Ellipsis, useStatusOverlay, ListItem, Separator, H3 } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { ContentCopy } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import { useLazyQuery } from '@apollo/client/react'
import { trackEvent } from '@fs/zion-analytics'
import transl from './Metadata.transl'
import {
  useMetadata,
  useGroupDataForm,
  useLanguages,
  useGroupData,
  useCheckboxes,
  useImageOrganizeModes,
  useImageOrganizeContext,
} from '../../hooks'
import { EditableField, ListItemGroupData, LoadingWrapper, GlossaryTooltip } from '../../components'
import queries from '../../queries'
import generateCitation from '../../../../../shared/utils/generateCitation'
import OlibNotes from '../internalFields/components/olibNotes/OlibNotes'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import {
  renderTitleField,
  renderVolumeField,
  renderLanguageField,
  renderCreatorField,
  renderImageGroupNumber,
  renderParentGroupLinks,
} from './Metadata.utils'

const numberFormat = new Intl.NumberFormat(i18n.language)

const Metadata = ({ hiddenFields = [], copyable, errors, editable }) => {
  const { metadata, setTitle, setVolume, setLanguages, setArchivalReferenceNumber, setCreator } = useMetadata()
  const { deleteCoveragesMode } = useGroupDataForm()
  const { data: languagesData, getLanguagesFromCodes } = useLanguages()
  const { initialGroup, itemNumber, itemCount, loading, group } = useGroupData()
  const { focusedIndex } = useSharedBrowserStateContext()
  const { deleteMode, dispatch } = useCheckboxes()
  const showStatusOverlay = useStatusOverlay()
  const { query } = useQueryParams()
  const location = useLocation()
  const indexedPercentageFlag = useFeatureFlag('lightyear_sharedImageViewer_indexedPercentage')
  const [requestParentGroups, { data: parentGroupsResponse, loading: parentGroupsLoading }] = useLazyQuery(
    queries.parentGroups
  )
  const { isDivideMode } = useImageOrganizeModes()
  const { dividerOffsets } = useImageOrganizeContext()

  useEffect(() => {
    if (group.id && !parentGroupsResponse)
      requestParentGroups({ variables: { parentGroupType: 'VOLUME_SET', rmsId: group.id } })
  }, [group.id, parentGroupsResponse, requestParentGroups])

  const handleCreatorChange = useCallback(
    (selected) => {
      dispatch && dispatch({ type: 'SET_CREATOR_CHECKBOXES', value: selected })
    },
    [dispatch]
  )

  const languageTextItems = useMemo(() => getLanguagesFromCodes(metadata?.languages), [getLanguagesFromCodes, metadata])
  const imageCount = useMemo(() => group?.childCount, [group])
  const volumeSetTitleNumber = query.volumeSetSearch
    ? initialGroup.properties?.find((p) => p.name === 'titleNumber')?.values?.[0]
    : null

  const queryView = query?.view
  const citation = useMemo(() => {
    if (hiddenFields.includes('citation')) return null
    const { creator, custodian, place, imageGroupNumber } = metadata || {}

    return generateCitation({
      creator,
      custodian,
      imageCount,
      imageIndex: focusedIndex,
      location,
      queryView,
      imageGroupNumber,
      place,
    })
  }, [metadata, location, focusedIndex, imageCount, queryView, hiddenFields])

  const copyToClipboard = useCallback(
    async (text = '') => {
      const options = { dismissable: true, transient: true }
      try {
        await navigator.clipboard.writeText(text)
        const textToCopy = text.length > 20 ? `${text.substring(0, 17)}...` : text
        showStatusOverlay({
          ...options,
          message: transl.copySuccess(textToCopy),
          type: 'success',
        })
      } catch (error) {
        showStatusOverlay({
          ...options,
          message: error.message,
          type: 'error',
        })
      }
    },
    [showStatusOverlay]
  )

  const generateCopyButton = useCallback(
    (value, fieldName, overrideShow, onCopy) => {
      if ((!copyable || !value) && !overrideShow) return null

      return (
        <ListItem.IconButton
          data-testid={`copy-${fieldName}`}
          Icon={ContentCopy}
          onClick={async () => {
            await copyToClipboard(value)
            if (onCopy) onCopy()
          }}
          title={transl.copyToClipboard(fieldName)}
        />
      )
    },
    [copyToClipboard, copyable]
  )

  const generateIndexedComponent = () => {
    const { indexedChildCount, childCount } = group
    if (childCount === undefined || indexedChildCount === undefined) return null
    if (indexedChildCount > 0) {
      return (
        <ListItemGroupData
          overline={transl.indexed()}
          hyperlinks={[
            {
              text: transl.indexedTotal(
                null,
                numberFormat.format(indexedChildCount),
                numberFormat.format(childCount),
                Math.floor((indexedChildCount / childCount) * 100)
              ),
              to: `/search/record/results?q.filmNumber=${metadata?.imageGroupNumber}`,
            },
          ]}
          glossaryDescription={transl.glossary.imagesIndexed()}
        />
      )
    }

    return (
      <ListItemGroupData
        overline={transl.indexed()}
        items={[
          transl.indexedTotal(
            null,
            numberFormat.format(indexedChildCount),
            numberFormat.format(childCount),
            Math.floor((indexedChildCount / childCount) * 100)
          ),
        ]}
        glossaryDescription={transl.glossary.imagesIndexed()}
      />
    )
  }

  return (
    <>
      <H3 size="H5">{transl.sectionTitle()}</H3>
      <Separator />
      <LoadingWrapper loading={loading}>
        {renderTitleField({ metadata, editable, errors, setTitle, generateCopyButton })}
        {renderVolumeField({ metadata, editable, errors, setVolume, generateCopyButton })}
        {renderLanguageField({
          editable,
          metadata,
          languagesData,
          setLanguages,
          errors,
          generateCopyButton,
          languageTextItems,
        })}

        {/* IMAGES INDEXED */}
        {indexedPercentageFlag?.isOn && !editable && <>{generateIndexedComponent()}</>}

        {/* CREATOR */}
        {renderCreatorField({
          editable,
          metadata,
          deleteCoveragesMode,
          deleteMode,
          setCreator,
          handleCreatorChange,
          generateCopyButton,
        })}

        {/* CUSTODIAN */}
        {!editable && (
          <ListItemGroupData
            overline={transl.custodian()}
            hyperlinks={metadata?.custodian?.map((item) => getSearchResultsLink('custodian', item))}
            rightElement={generateCopyButton(metadata?.custodian, transl.custodian())}
            glossaryDescription={transl.glossary.custodian()}
          />
        )}

        {/* ARCHIVAL REFERENCE NUMBER */}
        {!hiddenFields.includes('archivalReferenceNumber') && (
          <EditableField
            editing={editable}
            value={metadata?.archivalReferenceNumber}
            onDebounce={setArchivalReferenceNumber}
            label={transl.archivalReferenceNumber()}
            error={errors?.indexOf('archivalReferenceNumber') > -1}
            multiValue
            helpText={transl.multiValueHelpText()}
            ElseElement={
              <ListItemGroupData
                overline={transl.archivalReferenceNumber()}
                items={[metadata?.archivalReferenceNumber]}
                rightElement={generateCopyButton(metadata?.archivalReferenceNumber, transl.archivalReferenceNumber())}
                glossaryDescription={transl.glossary.archivalReferenceNumber()}
              />
            }
          />
        )}

        {renderImageGroupNumber({
          editable,
          metadata,
          itemCount,
          itemNumber,
          isDivideMode,
          dividerOffsets,
        })}

        {/* FILM ITEM NOTES */}
        {!editable && (
          <>
            <div
              css={css`
                margin-top: 10px;
                margin-bottom: -6px;
              `}
            >
              <GlossaryTooltip
                overline={
                  <Paragraph as="div" size="xs">
                    <Ellipsis>{transl.filmItemNotes()}</Ellipsis>
                  </Paragraph>
                }
                description={transl.glossary.filmItemNotes()}
              />
            </div>
            <OlibNotes groupId={query?.groupId || group?.id} />
          </>
        )}

        {/* CITATION */}
        {!editable && (
          <ListItemGroupData
            overline={transl.citation()}
            items={[citation]}
            rightElement={
              citation === null
                ? null
                : generateCopyButton(citation, transl.citation(), true, () =>
                    trackEvent({
                      event_name: 'click_action',
                      link_name: 'metadata-citation-copied',
                    })
                  )
            }
            glossaryDescription={transl.glossary.citation()}
          />
        )}

        {/* TITLE NUMBER */}
        {volumeSetTitleNumber && !editable && (
          <ListItemGroupData items={[volumeSetTitleNumber]} overline={transl.titleNumber()} />
        )}

        {renderParentGroupLinks({ parentGroupsResponse, parentGroupsLoading })}
      </LoadingWrapper>
    </>
  )
}

Metadata.propTypes = {
  hiddenFields: PropTypes.arrayOf(PropTypes.string),
}

export function getSearchResultsLink(key, value) {
  if (!(key && value)) return null

  const appPath = '/records/images/search-results'

  return {
    text: value,
    to: `${appPath}?${key}=${value}`,
  }
}

export default Metadata
